// src/api/tileTokens.js
import { parseDatasetKey } from "../utils/parseDatasetKey";
import { TILE_PATH, tileUrl } from "./tileUrl";
import { useAuthStore } from "../stores/useAuthStore";
import { useProjectStore } from "../stores/useProjectStore";

const tokenCache = new Map();     // cacheKey -> { token, ck, exp }
const inflight = new Map();       // cacheKey -> Promise
const refreshTimers = new Map();  // cacheKey -> timeoutId
const registeredMaps = new Set();

// Debounce map for 401 bursts: cacheKey -> timeoutId
// Collapses N simultaneous tile 401s into a single refresh attempt.
const pendingRefreshTimers = new Map();

function nowSec() {
  return Math.floor(Date.now() / 1000);
}

/* ======================================================
   THE ONLY PLACE A CACHE KEY IS BUILT.
   ======================================================

   It was built inline in FIVE places, all spelling the same thing, and that was
   fine while they all spelled it the SAME same thing. The Operator Portal's
   reviewer map needs a token WITHOUT the active project -- a plan review is not
   project-scoped, and /tile/session answers a project the caller cannot resolve
   with 403 INVALID_PROJECT rather than falling back -- so the key gains a third
   component and five copies become five chances to add it four times.

   THE FAILURE THAT WOULD CAUSE LOOKS LIKE WORKING SOFTWARE. A refresh path
   computing `k:full` while the fetch stored `k:full:noproject` finds nothing,
   refetches, stores under its own key, and does it again on every call. Tiles
   render. Tokens churn forever. tileTokens.cacheKey.test.js asserts no inline
   construction survives.
====================================================== */
export function cacheKeyFor({ datasetKey, operatingAoiId = null, noProject = false }) {
  // The SUFFIX form, not a third always-present segment: every existing key in
  // a live session stays byte-identical, so this change cannot orphan a token
  // that is currently driving a map.
  return `${datasetKey}:${operatingAoiId || "full"}${noProject ? ":noproject" : ""}`;
}

/* ======================================================
   WHICH VARIANT IS LIVE, for the paths that only have a tile URL.

   Three of the five sites derive their key from a rendering tile's URL, which
   carries `dataset` and `operating_aoi` and nothing about the project decision.
   They cannot CONSTRUCT the key, because they do not know which variant the
   original fetch used -- and guessing wrong means refreshing an entry nothing
   is reading while the entry that IS being read expires.

   So they RESOLVE it: ask the cache which variant exists. That is a lookup
   against the live map rather than a second source of truth, and when neither
   exists there is nothing to refresh anyway, so the default is the ordinary one.
====================================================== */
function liveVariantFor(datasetKey, operatingAoiId) {
  for (const noProject of [false, true]) {
    const cacheKey = cacheKeyFor({ datasetKey, operatingAoiId, noProject });
    if (tokenCache.has(cacheKey) || inflight.has(cacheKey)) return { cacheKey, noProject };
  }
  return { cacheKey: cacheKeyFor({ datasetKey, operatingAoiId }), noProject: false };
}

/* ======================================================
   INTERNAL: apply a fresh token to all registered map sources
   matching datasetKey, preserving per-source AOI and URL scheme.
   Called from the background refresh path.
====================================================== */
// NO CALLER since 715a2224 (2026-06-03): kept, and exported only for its
// tile-URL test, until it is removed on its own.
export function applyTokenToRegisteredMaps(datasetKey, parsedKey, token, ck) {
  for (const m of registeredMaps) {
    // Stale entries can linger after compare-mode mapB unmounts.
    // MapLibre nulls `style` on remove; use it as a liveness signal.
    if (!m || !m.style) {
      registeredMaps.delete(m);
      continue;
    }
    for (const [srcId, dk] of (m.__datasetKeyBySource?.entries() ?? [])) {
      if (dk !== datasetKey) continue;

      const src = m.getSource(srcId);
      if (!src || typeof src.setTiles !== "function") continue;

      const liveTileUrl = src.tiles?.[0] || "";
      const srcAoiId = liveTileUrl
        ? new URL(liveTileUrl, window.location.origin).searchParams.get("operating_aoi") || null
        : null;

      // Relative whatever the source was: both branches here always built the
      // same relative path, which tileUrl does.
      //
      // IMPORTANT: tile_token is NOT in the URL anymore. It goes via the
      // X-Tile-Token header (added by MapLibre's transformRequest), which
      // keeps the URL stable across token refreshes. Stable URL = no
      // setTiles invalidation = no map flash.
      const newUrl = tileUrl(parsedKey.key, ck, { operatingAoi: srcAoiId });

      // console.log("[TileAuth] setTiles after 401 recovery", { srcId, newUrl });
      src.setTiles([newUrl]);
    }
  }
}

/* ======================================================
   INTERNAL: background refresh triggered by a tile 401.
   Separated from the fetch patch so the patch can return
   the 401 response immediately without blocking MapLibre's
   tile queue for the duration of the token round trip.
====================================================== */
async function refreshTileAuth(urlStr, refreshingCacheKeys) {
  const parsedUrl = new URL(urlStr, window.location.origin);
  const datasetKey = parsedUrl.searchParams.get("dataset");
  const existingAoiId = parsedUrl.searchParams.get("operating_aoi") || null;

  if (!datasetKey) return;

  const parsedKey = parseDatasetKey(datasetKey);
  if (!parsedKey) return;

  const { cacheKey, noProject } = liveVariantFor(parsedKey.key, existingAoiId);

  if (refreshingCacheKeys.has(cacheKey)) return;
  refreshingCacheKeys.add(cacheKey);

  // console.log("[TileAuth] tile 401 intercepted, refreshing", cacheKey);

  try {
    // Refresh the token in tokenCache. MapLibre's transformRequest will
    // pick up the new value via X-Tile-Token header on the next tile
    // request. MapLibre's built-in tile retry handles re-fetching the
    // 401'd tile — no setTiles nudge needed.
    await getOrFetchTileToken(datasetKey, {
      operatingAoiId: existingAoiId,
      force: true,
      // RESOLVED, NOT ASSUMED. Refreshing the other variant would leave the
      // rendering tiles' token to expire while a second one is kept warm.
      noProject,
    });
  } finally {
    refreshingCacheKeys.delete(cacheKey);
  }
}

/**
 * Look up the current tile token for a given tile URL. Called from
 * MapLibre's transformRequest to inject the X-Tile-Token header on
 * each tile request. Returns null if no cached token exists yet —
 * in that case the request will go out without the header and 401,
 * which is fine: the 401 fetch patch triggers a token fetch and
 * MapLibre retries.
 */
export function getTokenForTileUrl(tileUrl) {
  try {
    const parsed = new URL(tileUrl, window.location.origin);
    const datasetKey = parsed.searchParams.get("dataset");
    const operatingAoiId = parsed.searchParams.get("operating_aoi");
    if (!datasetKey) return null;

    const parsedKey = parseDatasetKey(datasetKey);
    if (!parsedKey) return null;

    const { cacheKey } = liveVariantFor(parsedKey.key, operatingAoiId);
    const cached = tokenCache.get(cacheKey);
    return cached?.token ?? null;
  } catch {
    return null;
  }
}

/* ======================================================
   INTERNAL: the one fetch patch (REM-705)
   MapLibre silently swallows raster tile 401s: they never surface as map
   "error" events with a status code. Patching fetch is the only reliable
   interception point.

   ONE PATCH PER PAGE. It was once installed per bound map and never taken
   off, so every compare map Explore made (and every SitePlanner, portal,
   review and WellFinder map) added a wrapper around fetch for good. Now the
   first bound map installs it, later maps join it, and the last map's
   "remove" restores fetch as it was. A wrapper someone else put over this one
   (nothing in the app does) is not unwrapped from under them: the patch is
   left beneath it and forgotten, so the next bound map installs one that is
   surely live rather than trusting a chain it cannot see.

   The patch returns the 401 response to MapLibre immediately so its tile
   queue is not blocked while the token round trip completes. The refresh
   runs in the background via refreshTileAuth(). A 100ms debounce collapses
   the burst of simultaneous tile 401s (often 10-20 at expiry) into a single
   refresh per cacheKey.
====================================================== */
// cacheKeys being refreshed, shared by the patch and every map's error handler.
const refreshingCacheKeys = new Set();
let patchedFetch = null;
let fetchBeneath = null;

function installFetchPatch() {
  if (patchedFetch) return;
  const beneath = window.fetch;
  fetchBeneath = beneath;
  patchedFetch = async function (input, init) {
    const response = await beneath(input, init);

    const urlStr = typeof input === "string" ? input : input?.url;

    // Match both absolute and relative tile URLs.
    // syncImageryLayer in useSyncRegistryLayers uses a relative path
    // (/tiles/...) while useSyncImageryLayers uses the full domain.
    //
    // TILE_PATH alone: the host-prefixed and bare "/tiles" checks this had
    // matched only paths under it, the gateway's being the only tile path.
    const isTileRequest = Boolean(urlStr?.includes(TILE_PATH));

    if (response.status === 401 && isTileRequest) {
      // Derive cacheKey early so debounce is per-dataset, not per-tile.
      const parsedUrl = new URL(urlStr, window.location.origin);
      const datasetKey = parsedUrl.searchParams.get("dataset");
      const existingAoiId = parsedUrl.searchParams.get("operating_aoi") || null;
      const parsedKey = datasetKey ? parseDatasetKey(datasetKey) : null;
      const cacheKey = parsedKey
        ? liveVariantFor(parsedKey.key, existingAoiId).cacheKey
        : null;

      if (cacheKey && !refreshingCacheKeys.has(cacheKey) && !pendingRefreshTimers.has(cacheKey)) {
        const t = setTimeout(() => {
          pendingRefreshTimers.delete(cacheKey);
          refreshTileAuth(urlStr, refreshingCacheKeys).catch((err) =>
            console.error("[TileAuth] background refresh failed", err)
          );
        }, 100);
        pendingRefreshTimers.set(cacheKey, t);
      }
    }

    // Return immediately — do not await the refresh.
    // MapLibre will mark the tile as errored; setTiles() will trigger a re-fetch.
    return response;
  };
  window.fetch = patchedFetch;
}

function removeFetchPatch() {
  if (!patchedFetch) return;
  if (window.fetch === patchedFetch) window.fetch = fetchBeneath;
  patchedFetch = null;
  fetchBeneath = null;
}

/* ======================================================
   PUBLIC: bind once per map
   - NEVER removes or replaces sources
   - ONLY updates tiles via setTiles()
   - the map leaves on its "remove" event; the last one out takes the
     fetch patch off
====================================================== */
export function bindTileAuthHandler(map) {
  if (map.__tileAuthBound) return;
  map.__tileAuthBound = true;
  registeredMaps.add(map);
  installFetchPatch();

  const onRemove = () => {
    map.off?.("remove", onRemove);
    registeredMaps.delete(map);
    if (registeredMaps.size === 0) removeFetchPatch();
  };
  map.on("remove", onRemove);

  // ── map.on("error") ─────────────────────────────────────────────
  // Kept as a fallback for non-raster or future MapLibre versions that
  // do surface tile auth errors. In practice this will not fire for
  // raster tile 401s with the current MapLibre version.
  map.on("error", async (e) => {
    const err = e?.error;
    const status = err?.status || err?.response?.status;
    const message = err?.message || "";

    const isAuthFailure =
      status === 401 ||
      status === 403 ||
      message.includes("401") ||
      message.includes("403");

    if (!isAuthFailure) return;

    const sourceId = e?.sourceId;
    if (!sourceId) return;

    const datasetKey = map.__datasetKeyBySource?.get(sourceId) || null;
    if (!datasetKey) {
      console.warn("[TileAuth] Missing datasetKey for source", sourceId);
      return;
    }

    const parsed = parseDatasetKey(datasetKey);
    if (!parsed) return;

    const datasetOnly = parsed.key;

    const src = map.getSource(sourceId);
    const liveTileUrl = src?.tiles?.[0] || "";
    const existingAoiId = liveTileUrl
      ? new URL(liveTileUrl, window.location.origin).searchParams.get("operating_aoi") || null
      : null;

    const { cacheKey, noProject } = liveVariantFor(datasetOnly, existingAoiId);

    if (refreshingCacheKeys.has(cacheKey)) return;
    refreshingCacheKeys.add(cacheKey);

    try {
      const cached = tokenCache.get(cacheKey);
      const force = !cached || cached.exp <= nowSec() + 10;
      // Refresh tokenCache; transformRequest will pick up the new
      // X-Tile-Token on the next tile request. No setTiles needed.
      await getOrFetchTileToken(datasetKey, {
        operatingAoiId: existingAoiId,
        force,
        noProject,
      });
    } catch (err2) {
      console.error("[TileAuth] token refresh failed (error event)", err2);
    } finally {
      refreshingCacheKeys.delete(cacheKey);
    }
  });
}

/* ======================================================
   PUBLIC: token fetch
====================================================== */
export async function getOrFetchTileToken(datasetKey, { operatingAoiId = null, force = false, noProject = false } = {}) {
  const parsed = parseDatasetKey(datasetKey);
  if (!parsed) throw new Error(`invalid_dataset_key:${datasetKey}`);

  const datasetOnly = parsed.key;
  const cacheKey = cacheKeyFor({ datasetKey: datasetOnly, operatingAoiId, noProject });

  const cached = tokenCache.get(cacheKey);

  if (!force && cached && cached.exp > nowSec() + 10) {
    return cached;
  }

  // Check inflight BEFORE any async work so concurrent callers always
  // join the same in-progress promise regardless of force flag.
  if (inflight.has(cacheKey)) {
    return inflight.get(cacheKey);
  }

  // Cancel any pending proactive timer immediately, before the async
  // fetch begins, so a concurrent force-refresh cannot race the timer
  // callback and schedule two new timers.
  const existingTimer = refreshTimers.get(cacheKey);
  if (existingTimer) {
    clearTimeout(existingTimer);
    refreshTimers.delete(cacheKey);
  }

  // Build and register the inflight promise atomically — before any await —
  // so every concurrent caller joins this same promise.
  const p = (async () => {
    const store = useAuthStore.getState();
    const token = await store.ensureFreshToken();

    if (!token) {
      throw new Error("no_auth_token");
    }

    // NO PROJECT WHEN THE CALLER SAYS SO. The Operator Portal's reviewer map
    // has no project: a plan review is not project-scoped, and a reviewer who
    // happens to have one selected in the platform shell would otherwise send
    // it here -- where authz-api validates it and answers 403 INVALID_PROJECT
    // rather than falling back (index.js:1178-1189). Omitting it resolves
    // entitlement normally.
    const projectId = noProject ? null : useProjectStore.getState().activeProjectId;

    const url =
      `/tile/session?dataset=${encodeURIComponent(datasetOnly)}` +
      (operatingAoiId
        ? `&operating_aoi=${encodeURIComponent(operatingAoiId)}`
        : "") +
      (projectId && projectId !== "global"
        ? `&project_id=${encodeURIComponent(projectId)}`
        : "");

    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      tokenCache.delete(cacheKey);
      throw new Error(`tile_session_${res.status}`);
    }

    const json = await res.json();

    const value = {
      token: json.tile_token,
      ck: json.tile_cache_key,
      exp: nowSec() + Number(json.expires_in || 0),
      // What the session says about the capture (authz-api datasetFacts, DI4):
      // a map labels a synthetic_test one and stops its source at maxzoom; it
      // starts it at minzoom (S-e5), where the tiler stops answering empty tiles.
      provenance: json.provenance ?? null,
      minzoom: Number.isInteger(json.minzoom) ? json.minzoom : null,
      maxzoom: Number.isInteger(json.maxzoom) ? json.maxzoom : null,
    };

    tokenCache.set(cacheKey, value);

    // Schedule next proactive refresh from a clean state.
    const refreshInSec = value.exp - nowSec() - 30;

    if (refreshInSec > 0) {
      const timeoutId = setTimeout(async () => {
        try {
          // THE SITE MOST LIKELY TO BE MISSED, and it is inside this function
          // rather than at a call site: without `noProject` the timer refetches
          // under a DIFFERENT key on every tick, forever, while the entry the
          // map is reading expires untouched.
          const { token, ck } = await getOrFetchTileToken(datasetKey, {
            operatingAoiId,
            force: true,
            noProject,
          });
        } catch (err) {
          console.error("[TileAuth] proactive refresh failed", err);
        }
      }, refreshInSec * 1000);

      refreshTimers.set(cacheKey, timeoutId);
    }

    return value;
  })().finally(() => inflight.delete(cacheKey));

  // Registered synchronously before any await resolves
  inflight.set(cacheKey, p);
  return p;
}
